import { SettingsIcon } from "@xola/icons";
import { Badge, Checkbox, Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import { getDemographicCatalog, isEquipmentBased, isExperienceProduct, isGiftOfferProduct, isScheduled } from "common";
import { concat, find, groupBy, intersection, isEqual, List, sortBy } from "lodash";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Experience,
    PricingType,
    Product,
    productMap,
    ProductObject,
    productTitleMap,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkTag,
} from "types";
import { useDebounce } from "use-debounce";
import { Loader } from "../Loader";
import { NoResults } from "./NoResults";
import { NoTranslate } from "./NoTranslate";
import { ProductImage } from "./ProductImage";
import { SearchInput } from "./SearchInput";
import { SecondaryButton } from "./SecondaryButton";
import { PartnerSellerBadge } from "@/components/Settings/ButtonCode/PartnerBadge";
import { useDeletedExperiences } from "@/hooks/resources/useDeletedExperiences";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { SelectionState, useSelection } from "@/hooks/useSelection";
import { isExperiencePublished } from "@/models/Experience";
import { getSeller } from "@/utils/getSeller";

interface ListItem {
    id: ID;
    name: string;
}

type GroupByKey = "object";

type ProductGroup = {
    [key in GroupByKey]: string;
} & {
    products: Product[];
};

interface ExtendedListGroup {
    name: string;
    items: ListItem[];
}

type ExtendedListGroupOrItem = ExtendedListGroup | ListItem;

interface ExtendedList {
    prepend: ExtendedListGroupOrItem[];
    append: ExtendedListGroupOrItem[];
}

export enum ProductGroupType {
    ExperienceGiftOffer = "experience_gift_offer",
}

export interface ProductSelectorProps {
    value: SelectionState;
    onChange: (value: SelectionState) => void;
    collections?: ProductObject[];
    className?: string;
    classNames?: { list?: string };
    enableSelectAll?: boolean;
    enableGroupSelection?: boolean;
    partnerExperiences?: boolean;
    allowOnlyCommonDemographic?: boolean; // Experiences which has only common demographic(s) can be selectable
    hideDraft?: boolean; // Hide Draft Experiences which has no pricing
    hideUnScheduled?: boolean; // Hide UnScheduled Experiences
    disablePerOutingPrice?: boolean; // Disable Experiences with Per Outing pricing
    includeDeletedExperiences?: boolean; // Include deleted experiences to show in the custom reports filters
    singleSelect?: boolean; // Allow only one product to be selected

    /** @supports `object` */
    groupBy?: GroupByKey;

    /** IDs or product groups that needs to be excluded from the list */
    exclude?: Array<ID | ProductGroupType>;

    /**
     * Product IDs that should be shown but disabled, keyed by id → tooltip reason.
     * Pass a plain string to also show a settings button linking to the experience's pricing page,
     * or `{ reason, showSettingsButton: false }` for a tooltip-only reason with no settings button.
     */
    disabledReasons?: Record<ID, string | { reason: string; showSettingsButton?: boolean }>;

    /** Extra list items that can be injected in the ProductSelector */
    extendedList?: Partial<ExtendedList>;

    /** Only products for which this returns true are shown in the list */
    filterProduct?: (product: Product) => boolean;
}

const defaultValue: SelectionState = {
    all: true,
    selected: [],
};

export const ProductSelector = ({
    value = defaultValue,
    onChange,
    collections,
    className,
    classNames,
    enableSelectAll = true,
    enableGroupSelection,
    partnerExperiences = false,
    allowOnlyCommonDemographic = false,
    hideDraft = false,
    hideUnScheduled = false,
    disablePerOutingPrice = false,
    includeDeletedExperiences = false,
    singleSelect = false,
    groupBy,
    exclude,
    extendedList,
    disabledReasons,
    filterProduct,
}: ProductSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [name] = useDebounce(searchTerm, 500);

    // For cases when we allow only common demographics we need to have selected Products' data for comparison purposes
    const [selectedProducts, setSelectedProducts] = useState<ListItem[]>([]);
    // Track selected product for singleSelect
    const [selectedSingleProductId, setSelectedSingleProductId] = useState<string>();

    const isSingleCollection = collections?.length === 1;

    const fetchParams = {
        "name[contains]": name || null,
        ...(partnerExperiences && { partnerExperiences: partnerExperiences }),
        ...(!isSingleCollection && { "object[in]": collections ? collections.join(",") || null : null }),
    };
    const hasAnyValidParam = Object.values(fetchParams).some((value) => value !== null);

    const { data: activeProducts, isLoading: isLoadingProducts } = useAutoFetchCollection<Product>(
        isSingleCollection ? productMap[collections ? collections[0] : ProductObject.EXPERIENCE] : "products",
        hasAnyValidParam ? fetchParams : null,
        { keepPreviousData: true, enabled: !!collections },
    );

    const { deletedExperiences } = useDeletedExperiences(null, { enabled: includeDeletedExperiences });

    const products = useMemo(() => {
        return includeDeletedExperiences ? concat(activeProducts, deletedExperiences) : activeProducts;
    }, [activeProducts, deletedExperiences, includeDeletedExperiences]);

    let filteredProducts = searchTerm
        ? products.filter((product) => product.name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
        : products;

    filteredProducts = filteredProducts.filter((product) => {
        if (exclude?.includes(product.id)) {
            return false;
        }

        if (
            exclude?.includes(ProductGroupType.ExperienceGiftOffer) &&
            isGiftOfferProduct(product) &&
            product.experience
        ) {
            return false;
        }

        if (isExperienceProduct(product)) {
            if (hideDraft && !isExperiencePublished(product)) {
                return false;
            }

            if (hideUnScheduled && !isScheduled(product)) {
                return false;
            }
        }

        if (filterProduct && !filterProduct(product)) {
            return false;
        }

        return true;
    });

    const { data: purchaseRuleLinks } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "tags._id": PurchaseRuleLinkTag.PRICING,
            "entities.id[in]": filteredProducts.map((product) => product.id).join(","),
            expand: "purchaseRule",
        },
        { enabled: disablePerOutingPrice && filteredProducts.length > 0 },
    );

    const isFiltered = searchTerm.length > 0;
    const isSubset = enableSelectAll ? isFiltered : true;
    const defaultSelection = useMemo(() => ({ all: value.all, selected: value.selected }), [value]);

    const selectionItems = useMemo(() => {
        const extraItems: ListItem[] = [];

        if (extendedList?.prepend) {
            extraItems.push(...getListItems(extendedList.prepend));
        }

        if (extendedList?.append) {
            extraItems.push(...getListItems(extendedList.append));
        }

        return [...filteredProducts, ...extraItems];
    }, [filteredProducts, extendedList?.prepend, extendedList?.append]);

    const selection = useSelection(selectionItems, isSubset, defaultSelection);

    useEffect(() => {
        // TODO: This effect creates a cyclic dependencies without deep compare, refactor or find a better way for it
        if (!isEqual(selection.state, value)) {
            onChange({ all: selection.state.all, selected: selection.state.selected });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.state]);

    useEffect(() => {
        if (allowOnlyCommonDemographic) {
            setSelectedProducts(filteredProducts.filter((product) => value.selected.includes(product.id)));
        }

        if (singleSelect && value.selected.length > 0) {
            setSelectedSingleProductId(value.selected[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.selected]);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    const commonDemographicsTemplateIds = allowOnlyCommonDemographic
        ? getCommonDemographicsTemplateIds(
              [...filteredProducts, ...selectedProducts].filter(
                  (product) => isExperienceProduct(product) && selection.isSelected(product),
              ) as Experience[],
          )
        : [];

    useEffect(() => {
        if (allowOnlyCommonDemographic && commonDemographicsTemplateIds.length === 0) {
            // Reset the state if we switch to allowOnlyCommonDemographic mode and there is no common demographic
            selection.setState({ all: false, selected: [] });
            setSelectedProducts([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowOnlyCommonDemographic]);

    if (isLoadingProducts) {
        return <Loader />;
    }

    return (
        <div className={clsx("ui-product-selector overflow-hidden rounded-lg border border-gray-lighter", className)}>
            <div className="space-y-3 border-b border-b-gray-lighter bg-white p-3">
                <SearchInput className="w-full" onChange={handleSearchChange} />
            </div>

            {!singleSelect && enableSelectAll ? (
                <div className="flex items-center justify-between bg-gray-lighter py-2 px-3">
                    <Checkbox
                        disabled={products.length === 0}
                        checked={selection.isSelectedAll}
                        classNames={{
                            label: "font-bold tracking-tightest !text-black",
                        }}
                        label="Select All"
                        data-testid="experience-selector-select-all-checkbox"
                        onChange={() => selection.selectAll()}
                    />

                    <Badge className="text-gray-darker">
                        {selection.count === "All" ? (
                            <span>All Selected</span>
                        ) : (
                            <span>
                                <var data-var="selectionCount">{selection.count}</var> Selected
                            </span>
                        )}
                    </Badge>
                </div>
            ) : null}

            <div
                className={clsx(
                    "h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden bg-white",
                    classNames?.list,
                )}
            >
                <ProductSelectorExtendedList
                    groupBy={groupBy}
                    products={filteredProducts}
                    extendedList={extendedList}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    selectedSingleProductId={selectedSingleProductId}
                    setSelectedSingleProductId={setSelectedSingleProductId}
                    selection={selection}
                    singleSelect={singleSelect}
                    enableGroupSelection={enableGroupSelection}
                    commonDemographicsTemplateIds={commonDemographicsTemplateIds}
                    allowOnlyCommonDemographic={allowOnlyCommonDemographic}
                    purchaseRuleLinks={purchaseRuleLinks}
                    disablePerOutingPrice={disablePerOutingPrice}
                    disabledReasons={disabledReasons}
                />
            </div>
        </div>
    );
};

interface ProductSelectorListProps
    extends Omit<ProductCheckboxProps, "product">,
        Pick<ProductSelectorProps, "groupBy" | "enableGroupSelection"> {
    products: Product[];
}

const ProductSelectorList = ({ products, groupBy: groupByKey, ...rest }: ProductSelectorListProps) => {
    if (products.length === 0) {
        return <NoResults>No results found</NoResults>;
    }

    if (groupByKey) {
        const groups = getProductGroups(products, groupByKey);

        return (
            <>
                {groups.map((group) => {
                    const groupByValue = group[groupByKey];
                    const title =
                        groupByKey === "object" ? productTitleMap[groupByValue as ProductObject] : groupByValue;

                    return (
                        <ProductSelectorGroup key={groupByValue} title={title} products={group.products} {...rest} />
                    );
                })}
            </>
        );
    }

    return (
        <>
            {sortBy(products, "name").map((product: Product) => (
                <ProductCheckbox key={product.id} {...rest} product={product} />
            ))}
        </>
    );
};

interface ProductSelectorExtendedListProps
    extends ProductSelectorListProps,
        Pick<ProductSelectorProps, "extendedList"> {}

const ProductSelectorExtendedList = ({ extendedList, ...rest }: ProductSelectorExtendedListProps) => {
    const listItemComponent = (data: ExtendedListGroupOrItem) => {
        if ("items" in data) {
            return <ProductSelectorGroup key={data.name} title={data.name} {...rest} products={data.items} />;
        }

        return <ProductCheckbox key={data.id} {...rest} product={data} />;
    };

    return (
        <>
            {extendedList?.prepend && extendedList.prepend.map((groupOrItem) => listItemComponent(groupOrItem))}
            <ProductSelectorList {...rest} />
            {extendedList?.append && extendedList.append.map((groupOrItem) => listItemComponent(groupOrItem))}
        </>
    );
};

interface ProductSelectorGroupProps
    extends Omit<ProductCheckboxProps, "product">,
        Pick<ProductSelectorProps, "enableGroupSelection"> {
    products: Array<Product | ListItem>;
    title: string;
}

const ProductSelectorGroup = ({
    title,
    products,
    selection,
    singleSelect,
    enableGroupSelection = true,
    ...rest
}: ProductSelectorGroupProps) => {
    const productIds = products.map((product) => product.id);

    const isChecked = useMemo(() => {
        if (singleSelect) return false;

        if (selection.state.all) {
            return true;
        }

        return productIds.every((productId) => selection.state.selected.includes(productId));
    }, [selection.state, singleSelect, productIds]);

    const handleChange = () => {
        if (isChecked) {
            selection.deselectFromSelection(products);
            return;
        }

        selection.addToSelection(products);
    };

    return (
        <>
            <div className="bg-gray-light px-3 py-2">
                {enableGroupSelection && !singleSelect ? (
                    <Checkbox
                        disabled={singleSelect}
                        checked={isChecked}
                        classNames={{
                            label: "font-bold tracking-tightest !text-black",
                        }}
                        label={title}
                        data-testid="experience-selector-select-all-checkbox"
                        onChange={handleChange}
                    />
                ) : (
                    <h5 className="font-bold">{title}</h5>
                )}
            </div>

            {sortBy(products, "name").map((product) => (
                <ProductCheckbox
                    key={product.id}
                    singleSelect={singleSelect}
                    selection={selection}
                    {...rest}
                    product={product}
                />
            ))}
        </>
    );
};

interface ProductCheckboxProps
    extends Pick<
        ProductSelectorProps,
        "singleSelect" | "allowOnlyCommonDemographic" | "disablePerOutingPrice" | "disabledReasons"
    > {
    selection: ReturnType<typeof useSelection>;
    product: Product | ListItem;
    commonDemographicsTemplateIds: string[];
    purchaseRuleLinks: PurchaseRuleLink[];
    selectedProducts: ListItem[];
    setSelectedProducts: Dispatch<SetStateAction<ListItem[]>>;
    selectedSingleProductId?: ID;
    setSelectedSingleProductId: Dispatch<SetStateAction<ID | undefined>>;
}

const ProductCheckbox = ({
    selection,
    product,
    commonDemographicsTemplateIds,
    purchaseRuleLinks,
    selectedProducts,
    setSelectedProducts,
    selectedSingleProductId,
    setSelectedSingleProductId,
    singleSelect = false,
    allowOnlyCommonDemographic = false,
    disablePerOutingPrice = false,
    disabledReasons,
}: ProductCheckboxProps) => {
    const noCommonDemographic =
        isProductListItem(product) &&
        allowOnlyCommonDemographic &&
        selection.state.selected.length > 0 &&
        !hasCommonDemographic(product, commonDemographicsTemplateIds);

    const isPerOutingPrice = disablePerOutingPrice && hasPerOutingPrice(purchaseRuleLinks, product.id);

    let isDisabled = isProductListItem(product) && (noCommonDemographic || isPerOutingPrice);
    let disabledReason = noCommonDemographic
        ? "Experiences must share at least one demographic to make a package"
        : "Experience price type must be per person to be part of package";
    let showSettingsButton = false;

    const externalDisabledReason = disabledReasons?.[product.id];
    if (externalDisabledReason) {
        isDisabled = true;
        disabledReason =
            typeof externalDisabledReason === "string" ? externalDisabledReason : externalDisabledReason.reason;
        showSettingsButton =
            typeof externalDisabledReason === "string" ? true : externalDisabledReason.showSettingsButton ?? true;
    } else if (singleSelect && selectedSingleProductId && selectedSingleProductId !== product.id) {
        isDisabled = true;
        disabledReason = "Only one product can be selected";
    }

    const handleSingleSelectCheckboxChange = (product: ListItem) => {
        const isAlreadySelected = selectedSingleProductId === product.id;
        setSelectedSingleProductId(isAlreadySelected ? undefined : product.id);
        selection.setState({ all: false, selected: isAlreadySelected ? [] : [product.id] });
        setSelectedProducts(isAlreadySelected ? [] : [product]);
    };

    const handleCheckboxChange = (product: ListItem) => {
        if (singleSelect) {
            handleSingleSelectCheckboxChange(product);
            return;
        }

        selection.select(product);

        if (!allowOnlyCommonDemographic) {
            return;
        }

        if (selectedProducts.some((item) => item.id === product.id)) {
            setSelectedProducts([...selectedProducts.filter((item) => item.id !== product.id)]);
        } else {
            setSelectedProducts([...selectedProducts, product]);
        }
    };

    return (
        <Checkbox
            key={product.id}
            checked={selection.isSelected(product)}
            name={product.id}
            label={
                <ProductCheckboxContent
                    product={product}
                    disabled={isDisabled}
                    disabledReason={disabledReason}
                    showSettingsButton={showSettingsButton}
                />
            }
            disabled={isDisabled}
            className={clsx(
                "px-3 py-2 hover:cursor-pointer",
                selection.isSelected(product) ? "bg-blue-lighter" : "hover:bg-gray-hover",
            )}
            classNames={{ checkbox: "!border !border-gray" }}
            onChange={() => handleCheckboxChange(product)}
        />
    );
};

const ProductCheckboxContent = ({
    product,
    disabled,
    disabledReason,
    showSettingsButton,
}: {
    product: Product | ListItem;
    disabled: boolean;
    disabledReason: string;
    showSettingsButton: boolean;
}) => {
    const seller = getSeller();
    const history = useHistory();
    const isPartnerProduct = isExperienceProduct(product) && product.seller.id !== seller.id;

    const handleSettingsClick = useMemoizedFn((e: React.MouseEvent) => {
        e.stopPropagation();
        history.push(`/products/experiences/${product.id}/pricing`);
    });

    return (
        <span data-id={product.id} className={clsx("flex items-center", !disabled && "hover:cursor-pointer")}>
            {isProductListItem(product) && <ProductImage className="mr-2" product={product} size="tiny" />}
            <Tooltip content={disabled && disabledReason} disabled={!disabled}>
                <NoTranslate className={clsx("text-black", disabled && "text-gray-dark")}>
                    {product.name.trim()}
                </NoTranslate>
            </Tooltip>
            {disabled && disabledReason && showSettingsButton && (
                <span className="ml-1">
                    <SecondaryButton size="tiny" onClick={handleSettingsClick}>
                        <SettingsIcon />
                    </SecondaryButton>
                </span>
            )}
            {isPartnerProduct && (
                <span className="ml-2">
                    <PartnerSellerBadge sellerId={product.seller.id} />
                </span>
            )}
        </span>
    );
};

const getCommonDemographicsTemplateIds = (experiences: Experience[]) => {
    const commonDemographicsTemplateIds: Array<List<unknown>> = [];
    // This will be a array of arrays (each nested array will be a list of demographics template Ids of an experience)
    for (const experience of experiences) {
        commonDemographicsTemplateIds.push(getDemographicsTemplateIds(experience));
    }

    // intersection of for each array of demographics Ids, to get common Demographics Ids of all given experiences
    return intersection.apply(this, commonDemographicsTemplateIds) as string[];
};

const getDemographicsTemplateIds = (experience: Experience) => {
    return getDemographicCatalog(experience).map((demographic) => demographic.template?.id);
};

const hasCommonDemographic = (product: Product, commonDemographicsTemplateIds: string[]) => {
    if (isExperienceProduct(product)) {
        return intersection(commonDemographicsTemplateIds, getDemographicsTemplateIds(product)).length > 0;
    }

    return false;
};

const hasPerOutingPrice = (purchaseRuleLinks: PurchaseRuleLink[], id: ID) => {
    // purchaseRuleLink for this Product id
    const purchaseRuleLink = purchaseRuleLinks.find((purchaseRuleLink) => find(purchaseRuleLink.entities, { id }));

    if (purchaseRuleLink?.purchaseRules) {
        const perOutingPrice = purchaseRuleLink.purchaseRules.find((purchaseRule) => {
            const expandedPurchaseRule = purchaseRule.purchaseRule as PurchaseRule;
            return find(expandedPurchaseRule.tags, { id: PricingType.PER_OUTING });
        });

        return !!perOutingPrice;
    }

    return false;
};

const getProductGroups = (products: Product[], groupByKey: GroupByKey): ProductGroup[] => {
    const groupedProducts = groupBy(products, groupByKey);
    const groups: ProductGroup[] = [];

    for (const [key, products] of Object.entries(groupedProducts)) {
        groups.push({
            [groupByKey]: key,
            products,
        });
    }

    if (groupByKey === "object") {
        const groupByObjectSortOrder = [
            ProductObject.EXPERIENCE,
            ProductObject.GIFT_OFFER,
            ProductObject.MEMBERSHIP,
            ProductObject.PACKAGE,
            ProductObject.PARTNER_PACKAGE,
        ];

        return groups.sort((groupA, groupB) => {
            return (
                groupByObjectSortOrder.indexOf(groupA.object as ProductObject) -
                groupByObjectSortOrder.indexOf(groupB.object as ProductObject)
            );
        });
    }

    return groups;
};

const isProductListItem = (item: Product | ListItem): item is Product => {
    return !!(item as Product).object;
};

const getListItems = (data: ExtendedListGroupOrItem[]): ListItem[] => {
    const items: ListItem[] = [];

    for (const item of data) {
        if ("items" in item) {
            // `item` is a ListItem group
            items.push(...getListItems(item.items));
            continue;
        }

        items.push(item);
    }

    return items;
};

/**
 * Packages cannot contain equipment-based experiences, so mark those unselectable in any selector.
 */
export const getEquipmentDisabledReasons = (experiences: Experience[]) => {
    return Object.fromEntries(
        experiences
            .filter((experience) => isEquipmentBased(experience))
            .map((experience) => [
                experience.id,
                { reason: "Equipment based experiences cannot be included in a package", showSettingsButton: false },
            ]),
    );
};
